import React, { useCallback, useEffect, useState } from "react";
import { getRewardsByPartnerId, getAllUserRewards, updateUserReward } from "../api/rewardsAPI";
import { Bounce, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Gift, Award, CheckCircle2, Loader2, RefreshCw, Clock, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import config from "../config";

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

export default function ManageRewards() {
  const { userData } = useAuth();
  const { t } = useTranslation();

  const [rewards, setRewards] = useState([]);
  const [userRewards, setUserRewards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [updatingRewardId, setUpdatingRewardId] = useState(null);

  const fetchData = useCallback(async () => {
    if (!userData?.id) return;

    setIsLoading(true);
    try {
      const [partnerRewards, partnerUserRewards] = await Promise.all([
        getRewardsByPartnerId(userData.id),
        getAllUserRewards(userData.id),
      ]);

      setRewards(Array.isArray(partnerRewards) ? partnerRewards : []);
      setUserRewards(Array.isArray(partnerUserRewards) ? partnerUserRewards : []);
    } catch (error) {
      console.error("ManageRewards load error:", error);
      toast.error(t("manageRewardsLoadError", { defaultValue: "Unable to load rewards." }), {
        transition: Bounce,
      });
    } finally {
      setIsLoading(false);
    }
  }, [userData, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleMarkRedeemed = async (record) => {
    if (!record?.id) return;

    setUpdatingRewardId(record.id);
    try {
      const payload = {
        is_redeemed: true,
        redeemed_date: new Date().toISOString(),
      };
      const updated = await updateUserReward(record.id, payload);
      setUserRewards((current) =>
        current.map((item) => (item.id === record.id ? updated : item))
      );
      toast.success(t("rewardRedeemedSuccess", { defaultValue: "Marked as redeemed." }), {
        transition: Bounce,
      });
    } catch (error) {
      console.error("ManageRewards redeem error:", error);
      toast.error(t("rewardRedeemError", { defaultValue: "Unable to update reward." }), {
        transition: Bounce,
      });
    } finally {
      setUpdatingRewardId(null);
    }
  };

  const filteredUserRewards = userRewards.filter((record) => {
    const query = searchText.trim().toLowerCase();
    if (!query) return true;

    const user = record.user || {};
    const userName = (user.full_name || user.user_name || "").toString().toLowerCase();
    const userPhone = (user.user_mobile || user.user_phone || "").toString().toLowerCase();
    const recordId = (record.id || record._id || "").toString().toLowerCase();

    return (
      userName.includes(query) ||
      userPhone.includes(query) ||
      recordId.includes(query)
    );
  });

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t("manageRewards", { defaultValue: "Manage Rewards" })}</h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-500">
            {t(
              "manageRewardsSubtitle",
              {
                defaultValue:
                  "Review available partner rewards and update redemption status for won rewards.",
              }
            )}
          </p>
        </div>

        <button
          type="button"
          onClick={fetchData}
          className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-orange-300"
          disabled={isLoading}
        >
          {isLoading ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
          {t("refresh", { defaultValue: "Refresh" })}
        </button>
      </div>

      <section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
        <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Gift size={22} />
            {t("availableRewards", { defaultValue: "Available Rewards" })}
          </div>
          <div className="flex flex-wrap gap-3 text-sm text-gray-500">
            <span>{rewards.length} {t("items", { defaultValue: "items" })}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1">{t("partner", { defaultValue: "Partner" })}: {userData?.full_name || userData?.user_name || t("me", { defaultValue: "Me" })}</span>
          </div>
        </div>

        {isLoading ? (
          <div className="rounded-3xl border border-dashed border-gray-200 p-10 text-center text-gray-500">
            <Loader2 size={28} className="mx-auto mb-3 animate-spin" />
            {t("loadingRewards", { defaultValue: "Loading rewards..." })}
          </div>
        ) : rewards.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-gray-200 p-10 text-center text-gray-500">
            {t("noPartnerRewards", { defaultValue: "No rewards found for this partner." })}
          </div>
        ) : (
          <div className="grid gap-5 xl:grid-cols-2">
            {rewards.map((reward) => {
              const imageSrc = reward.photo
                ? reward.photo.startsWith("http")
                  ? reward.photo
                  : `${config.apiUrl}/assets/uploads/${reward.photo}`
                : null;
              const isActive = reward.is_active ?? (reward.end_date ? new Date(reward.end_date) > new Date() : true);

              return (
                <div key={reward.id ?? reward._id} className="overflow-hidden rounded-3xl border border-gray-200 bg-slate-50 shadow-sm">
                  {imageSrc ? (
                    <img
                      className="h-52 w-full object-cover"
                      src={imageSrc}
                      alt={reward.name || reward.title || "reward image"}
                      onError={(e) => { e.target.onerror = null; e.target.src = "/placeholder.png"; }}
                    />
                  ) : (
                    <div className="flex h-52 items-center justify-center bg-gray-100 text-sm text-gray-500">
                      {t("noImageAvailable", { defaultValue: "No image available" })}
                    </div>
                  )}

                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">{reward.name || reward.title || "Untitled reward"}</h3>
                        <p className="mt-2 text-sm leading-6 text-gray-600">{reward.description || "No description available."}</p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${isActive ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                        {isActive ? t("active", { defaultValue: "Active" }) : t("inactive", { defaultValue: "Inactive" })}
                      </span>
                    </div>

                    <div className="mt-5 grid gap-3 text-sm text-gray-700 sm:grid-cols-2">
                      <div className="rounded-2xl bg-white p-4 shadow-sm">
                        <p className="text-xs uppercase tracking-wide text-gray-500">{t("partner", { defaultValue: "Partner" })}</p>
                        <p className="mt-1 font-semibold text-gray-900">{reward.partner_name || reward.partner?.full_name || "-"}</p>
                        <p className="mt-1 text-xs text-gray-500">{reward.partner_contact || reward.partner?.user_email || "-"}</p>
                      </div>
                      <div className="rounded-2xl bg-white p-4 shadow-sm">
                        <p className="text-xs uppercase tracking-wide text-gray-500">{t("discount", { defaultValue: "Discount" })}</p>
                        <p className="mt-1 font-semibold text-gray-900">{reward.discount_value ?? reward.discount ?? "-"}%</p>
                        <p className="mt-1 text-xs text-gray-500">{t("redeemQuota", { defaultValue: "Redeem quota" })}: {reward.redeem_quota ?? reward.quota ?? "-"}</p>
                      </div>
                      <div className="rounded-2xl bg-white p-4 shadow-sm">
                        <p className="text-xs uppercase tracking-wide text-gray-500">{t("redeemed", { defaultValue: "Redeemed" })}</p>
                        <p className="mt-1 font-semibold text-gray-900">{reward.redeemed ?? 0}</p>
                        <p className="mt-1 text-xs text-gray-500">{t("remaining", { defaultValue: "Remaining" })}: {reward.redeem_quota ? Math.max(0, reward.redeem_quota - (reward.redeemed ?? 0)) : "-"}</p>
                      </div>
                      <div className="rounded-2xl bg-white p-4 shadow-sm">
                        <p className="text-xs uppercase tracking-wide text-gray-500">{t("validity", { defaultValue: "Validity" })}</p>
                        <p className="mt-1 font-semibold text-gray-900">{formatDate(reward.starting_date)} -</p>
                        <p className="text-xs text-gray-500">{formatDate(reward.end_date)}</p>
                      </div>
                    </div>

                    <div className="mt-5 grid gap-3 text-sm text-gray-500 sm:grid-cols-2">
                      <div>
                        <p className="text-xs uppercase tracking-wide">{t("createdAt", { defaultValue: "Created At" })}</p>
                        <p className="mt-1">{formatDate(reward.createdAt)}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide">{t("updatedAt", { defaultValue: "Updated At" })}</p>
                        <p className="mt-1">{formatDate(reward.updatedAt)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="mt-8 rounded-3xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Award size={22} />
            {t("userRewardsHistory", { defaultValue: "User Rewards History" })}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
            <div className="text-sm text-gray-500">{filteredUserRewards.length} / {userRewards.length} {t("records", { defaultValue: "records" })}</div>
            <label className="relative block w-full max-w-md">
              <span className="sr-only">{t("search", { defaultValue: "Search" })}</span>
              <span className="pointer-events-none absolute inset-y-0 left-3 inline-flex items-center text-gray-400">
                <Search size={16} />
              </span>
              <input
                type="search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={t("searchUserRewards", { defaultValue: "Search by user name, phone, or ID" })}
                className="w-full rounded-full border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 shadow-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-100"
              />
            </label>
          </div>
        </div>

        {isLoading ? (
          <div className="rounded-3xl border border-dashed border-gray-200 p-10 text-center text-gray-500">
            <Loader2 size={28} className="mx-auto mb-3 animate-spin" />
            {t("loadingUserRewards", { defaultValue: "Loading user rewards..." })}
          </div>
        ) : userRewards.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-gray-200 p-10 text-center text-gray-500">
            {t("noUserRewards", { defaultValue: "No user reward records found." })}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-3xl border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">{t("user", { defaultValue: "User" })}</th>
                  <th className="px-4 py-3">{t("reward", { defaultValue: "Reward" })}</th>
                  <th className="px-4 py-3">{t("machine", { defaultValue: "Machine" })}</th>
                  <th className="px-4 py-3">{t("gameMode", { defaultValue: "Game Mode" })}</th>
                  <th className="px-4 py-3">{t("rewardedDate", { defaultValue: "Rewarded Date" })}</th>
                  <th className="px-4 py-3">{t("status", { defaultValue: "Status" })}</th>
                  <th className="px-4 py-3">{t("action", { defaultValue: "Action" })}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {userRewards.map((record) => {
                  const user = record.user ?? {};
                  const reward = record.reward ?? {};
                  const machine = record.machine ?? {};
                  const gameMode = record.gameMode ?? {};
                  const redeemed = record.is_redeemed || record.redeemed || false;
                  const redeemedAt = record.redeemed_date || record.redeem_date || record.redeemed_date || null;
                  const rewardedAt = record.rewarded_date || record.createdAt || null;

                  return (
                    <tr key={record.id ?? record._id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 align-top text-sm text-gray-700">
                        <div className="font-semibold text-gray-900">{user.full_name || user.user_name || t("unknownUser", { defaultValue: "Unknown user" })}</div>
                        <div className="mt-1 text-xs text-gray-500">{user.user_email || user.email || "-"}</div>
                        <div className="mt-1 text-xs text-gray-500">{user.user_mobile || user.mobile || user.user_card || ""}</div>
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-gray-700">
                        <div className="font-semibold text-gray-900">{reward.name || reward.title || "-"}</div>
                        <div className="mt-1 text-xs text-gray-500">{reward.description || "-"}</div>
                        <div className="mt-2 text-xs text-gray-500">{t("discount", { defaultValue: "Discount" })}: {reward.discount_value ?? reward.discount ?? "-"}%</div>
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-gray-700">
                        <div className="font-semibold text-gray-900">{machine.type || machine.machineType || "-"}</div>
                        <div className="mt-1 text-xs text-gray-500">{machine.macAddress || machine.networkIP || "-"}</div>
                        <div className="mt-1 text-xs text-gray-500">{t("location", { defaultValue: "Location" })}: {machine.locationId ?? "-"}</div>
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-gray-700">
                        <div className="font-semibold text-gray-900">{gameMode.title || gameMode.name || "-"}</div>
                        <div className="mt-1 text-xs text-gray-500">{gameMode.description || "-"}</div>
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-gray-700">
                        <div>{formatDate(rewardedAt)}</div>
                        <div className="mt-1 text-xs text-gray-500">{t("createdAt", { defaultValue: "Created At" })}</div>
                      </td>
                      <td className="px-4 py-4 align-top text-sm">
                        <span className={`inline-flex max-w-max items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${redeemed ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                          {redeemed ? <CheckCircle2 size={14} /> : <Clock size={14} />}
                          {redeemed ? t("redeemed", { defaultValue: "Redeemed" }) : t("pending", { defaultValue: "Pending" })}
                        </span>
                        {redeemed && (
                          <div className="mt-1 text-xs text-gray-500">{formatDate(redeemedAt)}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 align-top text-sm">
                        <button
                          type="button"
                          disabled={redeemed || updatingRewardId === record.id}
                          onClick={() => handleMarkRedeemed(record)}
                          className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold transition ${redeemed ? "bg-gray-200 text-gray-700 cursor-not-allowed" : "bg-orange-500 text-white hover:bg-orange-600"}`}
                        >
                          {updatingRewardId === record.id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : redeemed ? (
                            t("alreadyRedeemed", { defaultValue: "Redeemed" })
                          ) : (
                            t("markRedeemed", { defaultValue: "Mark redeemed" })
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
